import { inject } from '@angular/core';
import { HttpErrorResponse, HttpContextToken, HttpInterceptorFn } from '@angular/common/http';

import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppError } from '../models';
import { NotificationFacade, LoggingService, AuthErrorHandlerService } from '@core/services';


export const HANDLE_ERROR = new HttpContextToken<boolean>(() => false);

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const isHandledLocally: boolean = req.context.get(HANDLE_ERROR);

  const clonedRequest = req.clone();
  const notificationFacade = inject(NotificationFacade);
  const loggingService = inject(LoggingService);
  const authErrorHandlerService = inject(AuthErrorHandlerService);

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isHandledLocally) {
        // rethrow error to be caught locally
        return throwError(() => error);
      }

      if ([401, 403].includes(error.status)) {
        loggingService.error(`[Error Interceptor] handle auth error:`, error);
        
        // Handle 401 errors by resetting state and redirecting
        if (error.status === 401) {
          authErrorHandlerService.handle401Error();
        }

        const authError = new AppError(
          "ErrorInterceptor",
          error.status,
          error.status === 401 ? "Unauthorized" : "Forbidden",
          error.status === 401 
            ? "Your session has expired. Please log in again." 
            : "You do not have permission to access this resource."
        );
        
        notificationFacade.dispatchAddError(authError);
        
        // Throw error so effects can catch it and components can handle it
        // Include status code in error message for easy detection
        const errorWithStatus = new Error(`${error.status}: ${error.status === 401 ? 'Unauthorized' : 'Forbidden'}`);
        (errorWithStatus as any).status = error.status;
        (errorWithStatus as any).originalError = error;
        return throwError(() => errorWithStatus);
      }

      const errorMsg = new AppError(
        "ErrorInterceptor",
        error.status,
        "Unhandled App Error",
        error.message);

      notificationFacade.dispatchAddError(errorMsg);
      loggingService.error('HTTP Error:', error.message);
      return throwError(() => new Error(error.message));
    })
  );
};